// lib/source-usage-client.ts
import type { SourceActionCompletion, SourceActionInput, SourceActionMetadata, SourceUsageAction, SourceUsageBatch, SourceUsageInterval } from '#types/types';
import { createSourceUsageClock, SOURCE_USAGE_FLUSH_MS } from '#lib/source-usage';
import { sourceActionSchema } from '#lib/source-actions';

export function startSourceUsageCollector(actorId: string, organisationId: string, isCurrent: () => boolean, isSource: () => boolean, getContext?: () => SourceActionMetadata) {
  const wallAnchor = Date.now(), monotonicAnchor = performance.now();
  const observedNow = () => Math.round(wallAnchor + performance.now() - monotonicAnchor);
  let clock: ReturnType<typeof createSourceUsageClock> | null = null;
  let source = false, stopped = false, suspended = false, inFlight = false, visitStart = false, retryAt = 0, nextSendAt = 0, failures = 0, dropped = 0;
  let pending: SourceUsageInterval[] = [];
  let sendingIds = new Set<string>();
  let previousContext: SourceActionMetadata | null = null;
  const attempts = new Map<string, number>();
  const queue = (record: SourceUsageInterval) => {
    pending.push(record);
    while (pending.length > 120) {
      const old = pending.shift()!;
      attempts.delete(old.id);
      dropped += 1 + old.droppedRecords;
    }
  };
  const enqueue = (boundary = false) => {
    if (!clock) return;
    const record = clock.take(crypto.randomUUID(), dropped);
    dropped = 0;
    const first = visitStart;
    visitStart = false;
    if (record.to === record.from && !record.droppedRecords) return;
    if (!first && !boundary && !record.visible.length && !record.active.length && !record.gapMs && !record.droppedRecords) return;
    queue(record);
  };
  const recordAction = (event: SourceUsageAction, visitId: string) => {
    if (stopped || !isCurrent()) return false;
    const parsed = sourceActionSchema.safeParse(event);
    if (!parsed.success) return false;
    let coalesced = false;
    if (event.action === 'search' || event.action === 'filter') {
      pending = pending.filter((record) => {
        if (record.visitId !== visitId || record.action?.action !== event.action || sendingIds.has(record.id) || attempts.has(record.id)) return true;
        if ((['entityType', 'entityId', 'chatSessionId', 'campaignId', 'tab'] as const).some((key) => record.action!.data[key] !== event.data[key])) return true;
        coalesced = true;
        dropped += record.droppedRecords;
        return false;
      });
    }
    const at = observedNow();
    const droppedRecords = dropped;
    dropped = 0;
    queue({ id: crypto.randomUUID(), visitId, from: at, to: at, visible: [], active: [], gapMs: 0, droppedRecords, action: parsed.data });
    return !coalesced;
  };
  const flush = async (keepalive = false) => {
    if (inFlight || performance.now() < Math.max(retryAt, nextSendAt) || !isCurrent()) return;
    const oldestAccepted = observedNow() - 24 * 60 * 60 * 1000;
    pending = pending.filter((record) => {
      if (record.from >= oldestAccepted) return true;
      dropped += 1 + record.droppedRecords;
      attempts.delete(record.id);
      return false;
    });
    if (!pending.length) return;
    const intervals: SourceUsageInterval[] = [];
    let body = '';
    for (const record of pending.slice(0, 20)) {
      const candidate: SourceUsageBatch = { sourceUsage: { version: 1, actorId, organisationId, intervals: [...intervals, record] } };
      const encoded = JSON.stringify(candidate);
      if (new TextEncoder().encode(encoded).length > 60 * 1024) break;
      intervals.push(record); body = encoded;
    }
    if (!intervals.length) return;
    const ids = new Set(intervals.map((record) => record.id));
    sendingIds = ids;
    inFlight = true;
    nextSendAt = performance.now() + 5_000;
    try {
      const response = await fetch('/api/events/sync-actions', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body, keepalive,
        signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) throw new Error(`Source measurement delivery failed (${response.status})`);
      pending = pending.filter((record) => !ids.has(record.id));
      for (const id of ids) attempts.delete(id);
      retryAt = 0; failures = 0;
    } catch {
      failures += 1;
      retryAt = performance.now() + Math.min(300_000, SOURCE_USAGE_FLUSH_MS * 2 ** Math.min(failures - 1, 4));
      pending = pending.filter((record) => {
        if (!ids.has(record.id)) return true;
        const count = (attempts.get(record.id) ?? 0) + 1;
        if (count < 8) { attempts.set(record.id, count); return true; }
        dropped += 1 + record.droppedRecords;
        attempts.delete(record.id);
        return false;
      });
      // Keep the failure observable without ever echoing identity or interaction content.
      if (failures === 1) console.warn('[Source measurement] Delivery delayed; usage coverage may be incomplete.');
    } finally { inFlight = false; sendingIds = new Set(); }
  };
  const sample = (input = false) => {
    if (stopped || !isCurrent()) return;
    const next = isSource();
    const now = performance.now();
    if (next && !source) { clock = createSourceUsageClock(crypto.randomUUID(), observedNow(), now); visitStart = true; }
    clock?.sample(now, next && !suspended && document.visibilityState === 'visible', next && !suspended && document.hasFocus(), input);
    if (next && clock && getContext && !suspended && document.visibilityState === 'visible') {
      const context = getContext();
      if (JSON.stringify(context) !== JSON.stringify(previousContext)) {
        const { tab: previousTab, ...previousTarget } = previousContext ?? {};
        const { tab, ...target } = context;
        const action = previousContext && JSON.stringify(previousTarget) === JSON.stringify(target) && previousTab !== tab ? 'tab' : 'navigation';
        recordAction({ action, origin: 'observed', phase: 'observed', operationId: crypto.randomUUID(), data: context }, clock.visitId);
        previousContext = context;
      }
    }
    if (source && !next) { enqueue(true); clock = null; void flush(); }
    if (!next) previousContext = null;
    source = next;
  };
  const interact = (event: Event) => { if (event.isTrusted) sample(true); };
  const transition = () => { sample(); enqueue(true); void flush(true); };
  const pagehide = () => { suspended = true; transition(); };
  const pageshow = () => { suspended = false; transition(); };
  // Browser-generated scroll events can be trusted even after programmatic scrollTo().
  const inputs = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
  for (const event of inputs) document.addEventListener(event, interact, { capture: true, passive: true });
  document.addEventListener('visibilitychange', transition);
  window.addEventListener('focus', transition);
  window.addEventListener('blur', transition);
  window.addEventListener('pagehide', pagehide);
  window.addEventListener('pageshow', pageshow);
  const timer = window.setInterval(() => { sample(); enqueue(); void flush(); }, SOURCE_USAGE_FLUSH_MS);
  sample();
  return {
    sample,
    beginAction(input: SourceActionInput): SourceActionCompletion | undefined {
      sample();
      if (!source || !clock || !isCurrent() || stopped) return undefined;
      const visitId = clock.visitId;
      const context = getContext?.();
      const changedTarget = input.data?.entityType && input.data.entityType !== context?.entityType;
      const data = { ...context, ...(changedTarget ? { entityId: undefined, tab: undefined } : {}), ...input.data };
      const operationId = crypto.randomUUID();
      const phase = ['navigation', 'tab', 'search', 'filter'].includes(input.action) ? 'observed' : 'requested';
      if (phase === 'observed' && (suspended || document.visibilityState !== 'visible')) return undefined;
      if (!recordAction({ ...input, data, operationId, phase }, visitId)) return undefined;
      let finished = phase === 'observed';
      return (outcome, counts) => {
        if (finished) return;
        if (recordAction({ ...input, data: { ...data, ...counts }, operationId, phase: outcome }, visitId)) finished = true;
      };
    },
    stop() {
      if (isCurrent()) { sample(); enqueue(true); void flush(true); }
      else { pending = []; attempts.clear(); }
      stopped = true;
      window.clearInterval(timer);
      for (const event of inputs) document.removeEventListener(event, interact, true);
      document.removeEventListener('visibilitychange', transition);
      window.removeEventListener('focus', transition);
      window.removeEventListener('blur', transition);
      window.removeEventListener('pagehide', pagehide);
      window.removeEventListener('pageshow', pageshow);
    },
  };
}
